import { Select, Spin, message } from "antd";
import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import {
  APIError,
  FinancialInstitution,
  FormFieldConfig,
  Product,
  SelectOptionsInterface,
  TransactionFields,
  TransactionFieldsEnum,
} from "../helpers/shared-interfaces";
import {
  createTransactionFieldsSelectOptions,
  generateDisplayErrorMessage,
  generateTierTypes,
  handleValidationErrors,
  mapAPIErrorToFormError,
  parseTransactionFields,
  request,
  responseHandler,
} from "../helpers/utilities";

import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";
import BaseForm from "../components/Form/BaseForm";
import { GET_PRODUCT_CATEGORIES, GET_PRODUCT_EXTENDED, UPDATE_PRODUCT } from "../gql/products";
import { GET_OPPORTUNITY_PROSPECTS } from "../gql/opportunity";
import { GET_FINANCIAL_INSTITUTIONS } from "../gql/general";
import { loanSecurityTypes } from "../helpers/constants";

interface State {
  id: string | number;
  name: string;
  productTierId: string;
  taxWaiverRequired: boolean;
  isLoading: boolean;
  opportunityProspectId: string;
  productCategoryId: string;
  financialInstitutions: FinancialInstitution[];
  vanCode: string;
  financialInstituitionId: string;
  prospects: SelectOptionsInterface[];
  categories: SelectOptionsInterface[];
  category: SelectOptionsInterface;
  prospect: SelectOptionsInterface;
  productTier: SelectOptionsInterface;
  fundCode: string;
  transactionFields: string[];
  selectedTransactionFields: TransactionFields;
  syncPrice: boolean
  isSubproduct: boolean;
  subproductId?: string
  mainProductId?: string
  isEzHomeProduct: boolean
  ezHomeFieldsToHide: string[]
  product?: Product
  isLoanOrFixedIncome: boolean;
  interestPerAnnum: number;
  commissionFee: number;
  processingFee: number;
  insuranceFee: number;
  maxAmount: number;
  maxTenor: number;
  minAmount: number;
  maxBalancePercentageAccessible: number;
  lienAmountMultiplier: number;
  isLoanProduct: boolean;
}

interface RouteParams {
  id: string;
  subproductId: string;
}
const { Option } = Select;

export function SelectOptionFactory(
  data: SelectOptionsInterface[],
): JSX.Element[] {
  return data.map((item: SelectOptionsInterface): JSX.Element => {
    return (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    );
  });
}

const ezHomeFields = [
  "developerName",
  "developerContact",
  "costOfProperty",
  "productFundingOption",
  "mortgageDetails",
  "specificationsOfProperty",
  "accruedBenefitDistribution",
  "accruedBenefitBalance",
];

const loanAndFixedIncomeFieldNameMap: Record<string, string> = {
  interestPerAnnum: "interest_per_annum",
  commissionFee: "commission_fee",
  processingFee: "processing_fee",
  insuranceFee: "insurance_fee",
  maxAmount: "max_amount",
  maxTenor: "max_tenor",
  minAmount: "min_amount",
  maxBalancePercentageAccessible: "max_balance_percentage_accessible",
  lienAmountMultiplier: "lien_amount_multiplier",
};

const loanOnlyFieldNameMap: Record<string, string> = {
  loanSecurityType: "loan_security_type",
};

const LoanAndFixedIncometransactionFields = [
  ...Object.keys(loanAndFixedIncomeFieldNameMap),
  "loanAndFixedIncomeSpacer",
];

const loanOnlyFields = ["loanSecurityType"];

export default class EditProduct extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      productTierId: "",
      taxWaiverRequired: false,
      opportunityProspectId: "",
      productCategoryId: "",
      isLoading: true,
      prospects: [],
      categories: [],
      financialInstitutions: [],
      id: "",
      prospect: {
        id: "",
        name: "",
      },
      category: {
        id: "",
        name: "",
      },
      productTier: {
        id: "",
        name: "",
      },
      vanCode: "",
      financialInstituitionId: "",
      fundCode: "",
      transactionFields: [],
      selectedTransactionFields: {},
      syncPrice: false,
      isSubproduct: false,
      isEzHomeProduct: false,
      ezHomeFieldsToHide: ezHomeFields,
      isLoanOrFixedIncome: false,
      interestPerAnnum: 0,
      commissionFee: 0,
      processingFee: 0,
      insuranceFee: 0,
      maxAmount: 0,
      maxTenor: 0,
      minAmount: 0,
      maxBalancePercentageAccessible: 0,
      lienAmountMultiplier: 0,
      isLoanProduct: false,
    };

    this.reference = React.createRef<FormInstance>();

    this.redirectToPage = this.redirectToPage.bind(this);
    this.getProductData = this.getProductData.bind(this);
    this.getProspectsAndCategories = this.getProspectsAndCategories.bind(this);
    this.updateProduct = this.updateProduct.bind(this);
    this.handleTransactionsFieldSelect =
      this.handleTransactionsFieldSelect.bind(this);
    this.getTransactionFields = this.getTransactionFields.bind(this);
    this.isLoanOrFixedIncomeProvider =
      this.isLoanOrFixedIncomeProvider.bind(this);
    this.isLoanProvider = this.isLoanProvider.bind(this);
    this.handleProductCategoryChange = this.handleProductCategoryChange.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  getProductData(): void {
    interface Response {
      product: Product;
    }

    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_PRODUCT_EXTENDED,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          const initialFields = this.getTransactionFields(
              data.data.product.transactionFields,
          );
          const productCategoryName = data.data.product.productCategory.name;
          const value = this.isLoanOrFixedIncomeProvider(productCategoryName);
          const isLoanProduct = this.isLoanProvider(productCategoryName);
          this.handleTransactionsFieldSelect(initialFields);
          if (!data.errors) {
            this.setState({
              product: data.data.product,
              name: data.data.product.name,
              vanCode: data.data.product.vanCode,
              financialInstituitionId:
                data.data.product.financialInstitution?.id,
              productTier: data.data.product.productTier,
              productTierId: data.data.product.productTier.id,
              category: data.data.product.productCategory,
              prospect: data.data.product.opportunityProspect,
              productCategoryId: data.data.product.productCategory.id,
              opportunityProspectId: data.data.product.opportunityProspect.id,
              fundCode: data.data.product.fundCode,
              taxWaiverRequired: data.data.product.taxWaiverRequired,
              transactionFields: initialFields,
              syncPrice: data.data.product?.syncPrice,
              isLoanOrFixedIncome: value,
              isLoanProduct,
              interestPerAnnum:
                data.data.product.transactionFields?.interest_per_annum,
              commissionFee:
                data.data.product.transactionFields?.commission_fee,
              insuranceFee: data.data.product.transactionFields?.insurance_fee,
              maxAmount: data.data.product.transactionFields?.max_amount,
              maxTenor: data.data.product.transactionFields?.max_tenor,
              minAmount: data.data.product.transactionFields?.min_amount,
              maxBalancePercentageAccessible:
                data.data.product.transactionFields
                  ?.max_balance_percentage_accessible,
              lienAmountMultiplier:
                data.data.product.transactionFields?.lien_amount_multiplier,
              processingFee:
                data.data.product.transactionFields?.processing_fee,
            });
          } else {
            message.error("Failed to Get product data");
          }
        });
      },
    );
  }

  getAllProspects = (): Promise<AxiosResponse> => {
    return request({
      query: GET_OPPORTUNITY_PROSPECTS,
    });
  };

  getAllCategories = (): Promise<AxiosResponse> => {
    return request({
      query: GET_PRODUCT_CATEGORIES,
      variables: {
        page: 1,
      },
    });
  };

  getFinancialInstitutionals = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async getProspectsAndCategories(): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const [prospects, categories]: any = await Promise.all([
        this.getAllProspects(),
        this.getAllCategories(),
      ]);
      this.setState({
        isLoading: false,
        prospects: prospects.data.data.opportunityProspects,
        categories: categories.data.data.productCategories.results,
      });
    } catch (e) { }
  }

  async updateProduct(): Promise<void> {
    const {
      id,
      syncPrice,
      selectedTransactionFields,
      mainProductId,
      isSubproduct,
      isLoanOrFixedIncome,
      isLoanProduct,
    } = this.state;

    interface Response {
      data?: {
        updateProduct: {
          product: SelectOptionsInterface;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "name",
        "opportunityProspectId",
        "productCategoryId",
        "productTierId",
        "fundCode",
        "financialInstitutionId",
        "vanCode",
        "taxWaiverRequired",
        "developerName",
        "developerContact",
        "costOfProperty",
        "productFundingOption",
        "mortgageDetails",
        "specificationsOfProperty",
        "accruedBenefitDistribution",
        "accruedBenefitBalance"
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const values = this.reference.current.getFieldsValue();
    const loanAndFixedIncomePayload = Object.fromEntries(
      Object.entries(loanAndFixedIncomeFieldNameMap)
        .map(([camelCaseKey, snakeCaseKey]) => [
          snakeCaseKey,
          values?.[camelCaseKey],
        ])
        .filter(([, value]) => value != null),
    );
    const loanOnlyPayload = isLoanProduct
      ? Object.fromEntries(
          Object.entries(loanOnlyFieldNameMap)
            .map(([camelCaseKey, snakeCaseKey]) => [
              snakeCaseKey,
              values?.[camelCaseKey],
            ])
            .filter(([, value]) => value != null),
        )
      : {};
    Object.assign(loanAndFixedIncomePayload, loanOnlyPayload);
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_PRODUCT,
        variables: {
          id,
          ...data,
          transactionFields: isLoanOrFixedIncome
            ? loanAndFixedIncomePayload
            : selectedTransactionFields,
          taxWaiverRequired: data.taxWaiverRequired === "yes" ? true : false,
          syncPrice,
          mainProductId: isSubproduct ? mainProductId : null
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateProduct",
      );


      if (handled) {
        message.error("An Error Occurred.");
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Product updated sucessfully");
        isSubproduct ? this.redirectToPage(`/products/${mainProductId}/subproducts/${id}`) : this.redirectToPage(`/products/${id}`);
      }
    } catch (error) {
      message.error(`Failed to update product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  getTransactionFields(
    transactionFields: TransactionFields | TransactionFields[] | string | null,
  ): string[] {
    return Object.keys(parseTransactionFields(transactionFields));
  }

  isLoanOrFixedIncomeProvider = (providerName?: string): boolean => {
    const name = (providerName || "").trim().toLowerCase();
    return name === "loans" || name === "fixed income";
  };

  isLoanProvider = (providerName?: string): boolean => {
    const name = (providerName || "").trim().toLowerCase();
    return name === "loans";
  };

  handleProductCategoryChange(value: string): void {
    const selectedCategory = this.state.categories.find(
      (category) => category.id === value,
    );
    const categoryName = selectedCategory?.name;

    this.setState({
      isLoanOrFixedIncome: this.isLoanOrFixedIncomeProvider(categoryName),
      isLoanProduct: this.isLoanProvider(categoryName),
    });
  }

  redirectToPage(path: string): void {
    this.props.history.push(path);
  }

  handleTransactionsFieldSelect = async (value: string | string[]): Promise<void> => {
    const values = value as unknown as string[];

    const transactionsHash: TransactionFields = values.reduce<TransactionFields>((acc, key) => {
      const label = (TransactionFieldsEnum as Record<string, string>)[key];
      if (label) acc[key] = label;
      return acc;
    }, {});

    this.setState({ selectedTransactionFields: transactionsHash })
  };

  changeSyncPrice = async (checkedValue: boolean) => {
    this.setState({
      syncPrice: checkedValue,
    });
  };

  componentDidMount() {
    const { id, subproductId } = this.props.match.params;
    const isSubproduct = !!subproductId;
    const productIdToUse = isSubproduct ? subproductId : id;

    this.setState({ id: productIdToUse, mainProductId: id, isSubproduct, subproductId }, () => {
      this.getProspectsAndCategories()
      this.getProductData()
      this.getFinancialInstitutionals()
    })
  }

  changeIsEzHomeProduct = (checkedValue: boolean) => {
    this.setState({
      ezHomeFieldsToHide: checkedValue ? [] : ezHomeFields,
      isEzHomeProduct: checkedValue,
    });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      prospects,
      categories,
      name,
      prospect,
      category,
      productTier,
      fundCode,
      transactionFields,
      taxWaiverRequired,
      financialInstituitionId,
      vanCode,
      financialInstitutions,
      syncPrice,
      isEzHomeProduct,
      ezHomeFieldsToHide,
      product,
      isLoanOrFixedIncome,
      isLoanProduct,
      interestPerAnnum,
      insuranceFee,
      commissionFee,
      maxAmount,
      minAmount,
      maxTenor,
      maxBalancePercentageAccessible,
      lienAmountMultiplier,
      processingFee,
    } = this.state;

    const initialTaxWaiverOption = taxWaiverRequired ? "yes" : "no";
    const institutions = financialInstitutions.map((institution) => {
      return { id: institution.id, name: institution.name };
    });
    if (!product) return <Spin spinning={true}></Spin>
    const fields: FormFieldConfig[] = [
      {
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        initialValue: name,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "opportunityProspectId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Prospect",
        placeholder: "Prospect",
        initialValue: prospect.id,
        selectOptions: prospects,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productCategoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Category",
        placeholder: "Category",
        initialValue: category.id,
        selectOptions: categories,
        changeHandler: this.handleProductCategoryChange,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "fundCode",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Fund Code",
        placeholder: "Fund Code",
        initialValue: fundCode,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productTierId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Tier",
        placeholder: "Tier",
        initialValue: productTier.id,
        selectOptions: generateTierTypes(),
        colConfig: {
          span: 7,
        },
      },
      {
        name: "taxWaiverRequired",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Is Tax Waiver Required?",
        placeholder: "Is Tax Waiver Required?",
        initialValue: initialTaxWaiverOption,
        selectOptions: [
          { name: "Yes", id: "yes" },
          { name: "No", id: "no" },
        ],
        colConfig: {
          span: 7,
        },
      },
      {
        id: "financialInstitutionId",
        name: "financialInstitutionId",
        placeholder: "Financial Institution",
        inputType: "select",
        selectOptions: institutions,
        initialValue: financialInstituitionId,
        rule: "string",
        label: "Institution Name",
        required: false,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "vanCode",
        rule: "string",
        inputType: "string",
        required: false,
        label: "VAN Code",
        initialValue: vanCode,
        placeholder: "VAN Code",
        colConfig: {
          span: 7,
        },
      },
      {
        isArrayField: true,
        id: "transactionFields",
        name: "transactionFields",
        inputType: "multipleSelect",
        label: "Transaction Fields",
        rule: "string",
        placeholder: "Select Transaction Fields",
        required: false,
        initialValue: transactionFields,
        changeHandler: this.handleTransactionsFieldSelect,
        colConfig: {
          span: 7,
        },
        selectOptions: createTransactionFieldsSelectOptions(),
        elementStyle: {
          marginBottom: "20px",
        },
      },
      {
        name: "interestPerAnnum",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Interest Per Annum %",
        initialValue: interestPerAnnum,
        placeholder: "Enter interest rate",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "commissionFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Commission Fee",
        initialValue: commissionFee,
        placeholder: "Enter commission fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "processingFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Processing Fee",
        initialValue: processingFee,
        placeholder: "Enter processing fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "insuranceFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Insurance Fee",
        initialValue: insuranceFee,
        placeholder: "Enter insurance fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxAmount",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Maximum Amount",
        initialValue: maxAmount,
        placeholder: "Enter amount",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "minAmount",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Minimum Amount",
        initialValue: minAmount,
        placeholder: "Enter minimum amount",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxTenor",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Maximum Tenor (months)",
        initialValue: maxTenor,
        placeholder: "Enter maximum tenor",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxBalancePercentageAccessible",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Max Balance % Accessible",
        initialValue: maxBalancePercentageAccessible,
        placeholder: "Enter max balance percentage",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "lienAmountMultiplier",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Lien Amount Multiplier",
        initialValue: lienAmountMultiplier,
        placeholder: "Enter lien amount multiplier",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "loanSecurityType",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Loan Security Type",
        placeholder: "Loan Security Type",
        initialValue: product?.transactionFields?.loan_security_type,
        selectOptions: loanSecurityTypes,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "isEzHomeProduct",
        name: "isEzHomeProduct",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Is EzHome Product?",
        checkboxCheckedValue: isEzHomeProduct ? true : false,
        checkboxChangeHandler: (e) => this.changeIsEzHomeProduct(e.target.checked),
        colConfig: {
          span: 7,
        },
      },
      {
        name: "developerName",
        id: "developerName",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Developer Name",
        placeholder: "Developer Name",
        initialValue: product?.developerName,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "developerContact",
        id: "developerContact",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Developer Contact",
        placeholder: "Developer Contact",
        initialValue: product?.developerContact,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productFundingOption",
        id: "productFundingOption",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Product Funding Option",
        placeholder: "Product Funding Option",
        initialValue: product?.productFundingOption,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "costOfProperty",
        id: "costOfProperty",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Cost of Property",
        placeholder: "Cost of Property",
        initialValue: product?.costOfProperty,
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "accruedBenefitBalance",
        id: "accruedBenefitBalance",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Accrued Benefit Balance",
        placeholder: "Accrued Benefit Balance",
        initialValue: product?.accruedBenefitBalance,
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        id: "accruedBenefitDistribution",
        name: "accruedBenefitDistribution",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Accrued Benefit Distribution",
        placeholder: "Cash balance, Property Value...",
        initialValue: product?.accruedBenefitDistribution,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "specificationsOfProperty",
        name: "specificationsOfProperty",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Specifications of Property",
        placeholder: "Location, size, type...",
        initialValue: product?.specificationsOfProperty,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "mortgageDetails",
        name: "mortgageDetails",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Mortgage Details",
        placeholder: "Mortgage Details",
        initialValue: product?.mortgageDetails,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "syncPrice",
        name: "syncPrice",
        required: false,
        inputType: "checkbox",
        rule: "boolean",
        label: "Sync Price?",
        checkboxCheckedValue: syncPrice ? true : false,
        checkboxChangeHandler: (e) => this.changeSyncPrice(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 5,
        },
      },
      {
        id: "updateProduct",
        name: "updateProductButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateProduct,
      },
    ];
    return (
      <Card header="">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              gutter={[16, 16]}
              reference={this.reference}
              fieldsToHide={[
                ...ezHomeFieldsToHide,  
                ...(isLoanOrFixedIncome
                  ? ["transactionFields", ...(isLoanProduct ? [] : loanOnlyFields)]
                  : [...LoanAndFixedIncometransactionFields, ...loanOnlyFields]),
              ]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
